/**
 * FilePreviewContent — window-independent document preview core.
 *
 * Given a PreviewFile, detects the type, fetches text content when needed,
 * and renders the matching renderer plus an optional compact view-options
 * toolbar. Shared by the FilePreviewApp window and the Drive 2.0 inline
 * preview pane so the renderer-selection logic lives in one place.
 *
 * Scope: documents (pdf, office, text, csv, json, md, xml), images, videos
 * (VideoRenderer: range streaming for Knowledge Area videos, jump to a
 * timecode), audio (the multimedia MediaPlayer, #1630) and HTML pages (the
 * sandboxed HtmlPreview, #1773) and BPMN diagrams (bpmn-js, #1777). Every format `classifyFile` calls
 * `unsupported` shows a download instead (#1611).
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownToLine, Download, Hash, WrapText } from 'lucide-react'
import { useTheme } from '@/hooks/useTheme'
import { DriveLoadingPlaceholder } from '@/components/common/DriveLoadingPlaceholder'
import { Button } from '@/components/ui/button'
import { MediaPlayer } from '@/components/applications/multimedia/MediaPlayer'
import { logError } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { JsonRenderer } from './renderers/JsonRenderer'
import { MarkdownRenderer } from './renderers/MarkdownRenderer'
import { CsvRenderer } from './renderers/CsvRenderer'
import { CodeRenderer } from './renderers/CodeRenderer'
import { PdfRenderer } from './renderers/PdfRenderer'
import { SheetPreview } from './renderers/SheetPreview'
import { ImageRenderer } from './renderers/ImageRenderer'
import { VideoRenderer, type SeekRequest } from './renderers/VideoRenderer'
import { isSheetPdfPreview, isTextFormat, needsOfficePdfPreview, previewFormat } from './fileType'
import { highlightWithin } from './renderers/domHighlight'
import { PreviewSearchBar } from './PreviewSearchBar'
import { usePreviewSearch } from './usePreviewSearch'
import { TextZoomControls } from './TextZoomControls'
import { useTextZoom } from './useTextZoom'
import type { FileType, PreviewFile } from './types'
import type { FileRef } from '@/lib/files/fileRef'
import { useFileRefPreview } from './useFileRefPreview'
import '@/styles/file-preview.css'

// Nothing in these is text the find bar could reach.
const UNSEARCHABLE_FORMATS: FileType[] = ['image', 'video', 'audio', 'bpmn']

// Lazy like every other caller: the viewer is only worth loading once a page
// is opened. Never render HTML here in any other way (AGENTS.md, hard rule 4).
const HtmlPreview = lazy(() =>
  import('@/components/applications/drive2/components/html/HtmlPreview').then((m) => ({
    default: m.HtmlPreview,
  })),
)

// bpmn-js is ~1 MB, so a static import would land in the main chunk.
const BpmnPreview = lazy(() =>
  import('@/components/applications/drive2/components/bpmn/BpmnPreview').then((m) => ({
    default: m.BpmnPreview,
  })),
)

function getSyntaxLanguage(fileName: string): string | undefined {
  const extension = fileName.toLowerCase().split('.').pop()
  if (extension === 'xml') return 'xml'
  return undefined
}

interface PreviewViewProps {
  file: PreviewFile
  className?: string
  /** Show the inline view-options toolbar (text/csv). Defaults to true. */
  showToolbar?: boolean
  /** Initial PDF fit mode. Unset, it follows the file type (cdb_webos#1477). */
  pdfDefaultFitMode?: 'width' | 'page'
  /** Initial PDF page (deep-linking). */
  pdfInitialPage?: number
  /** Called when the PDF page changes (deep-linking). */
  onPdfPageChange?: (page: number) => void
  /** Called when the PDF page count is known. */
  onPdfNumPagesChange?: (numPages: number) => void
  /** Start the find bar on this term (the Verifier's cited passage, #632). */
  seededSearchTerm?: string
  /** Extra classes for the find bar's row, to line it up with a neighbour. */
  findBarClassName?: string
  /** Video only: jump to this time; a new nonce repeats the jump. */
  mediaSeek?: SeekRequest
  /** Video only: the playback position, e.g. for the `t` deep-link param. */
  onMediaTimeChange?: (seconds: number) => void
  /** Audio only: the known length in seconds, for recordings whose WebM
   *  carries none until it is seeked. */
  mediaDuration?: number
  /** Download for a file without a preview, when the caller has no URL to
   *  copy from (Drive fetches no blob for it). */
  onDownload?: () => void
  /** Set while that download runs. */
  isDownloading?: boolean
}

function PreviewFileView({
  file,
  className,
  showToolbar = true,
  pdfDefaultFitMode,
  pdfInitialPage,
  onPdfPageChange,
  onPdfNumPagesChange,
  seededSearchTerm,
  findBarClassName,
  mediaSeek,
  onMediaTimeChange,
  mediaDuration,
  onDownload,
  isDownloading = false,
}: PreviewViewProps) {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const fileType = file.format ?? previewFormat(file.fileName)
  // The find bar appears only where there is text to find (#1656). The hits are
  // marked in the DOM; `usePreviewSearch` steps through them.
  const searchable = fileType !== null && !UNSEARCHABLE_FORMATS.includes(fileType)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const search = usePreviewSearch(rootRef, file.fileUrl, searchable ? seededSearchTerm : undefined)
  const term = searchable ? search.term : ''
  const [fileContent, setFileContent] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // View options
  const [wordWrap, setWordWrap] = useState(true)
  const [showLineNumbers, setShowLineNumbers] = useState(true)
  const [csvDelimiter, setCsvDelimiter] = useState<',' | ';' | '\t' | '|'>(',')
  const [csvHasHeader, setCsvHasHeader] = useState(true)

  // An HTML page opens rendered. A search term switches to the source: the find
  // bar cannot reach into the sandboxed frame, and the Verifier hands a term over
  // on its own (#632).
  const [htmlView, setHtmlView] = useState<'page' | 'source'>(seededSearchTerm ? 'source' : 'page')
  const [htmlViewBasis, setHtmlViewBasis] = useState({ fileUrl: file.fileUrl, term })
  // Adjusted during render, not in an effect: the source must be in the DOM
  // when the highlight effect below runs.
  if (htmlViewBasis.fileUrl !== file.fileUrl || htmlViewBasis.term !== term) {
    setHtmlViewBasis({ fileUrl: file.fileUrl, term })
    if (term) setHtmlView('source')
    else if (htmlViewBasis.fileUrl !== file.fileUrl) setHtmlView('page')
  }
  const showHtmlPage = fileType === 'html' && htmlView === 'page'
  const isDiagram = fileType === 'bpmn'
  // Hands the bytes already fetched to the viewer. Stable per content, because
  // HtmlPreview and BpmnPreview rebuild when this identity changes.
  const loadFetchedText = useCallback(async () => fileContent, [fileContent])

  const isDarkTheme = theme.includes('dark') || theme === 'dark'
  // Text zoom (#755). PDF brings its own page-aware zoom; images and sheets
  // have their own handling. Everything that renders text scales as a whole.
  const textZoom = useTextZoom(file.fileUrl)

  // Fetch text content for text-based types; binary types render from the URL.
  useEffect(() => {
    let active = true

    if (!isTextFormat(fileType)) {
      setFileContent('')
      setError(null)
      setLoading(false)
      return
    }

    const fetchText = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(file.fileUrl)
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const text = await response.text()
        if (active) setFileContent(text)
      } catch {
        if (active) setError(t('filePreview.errorLoading'))
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchText()
    return () => { active = false }
  }, [file.fileUrl, fileType, t])

  const handleNumPages = useCallback((numPages: number) => {
    onPdfNumPagesChange?.(numPages)
  }, [onPdfNumPagesChange])

  // Search for everything that is not a PDF (#627). The PDF renderer marks its
  // own text layer while rendering; markdown, code, CSV and JSON are already in
  // the DOM by then, so their hits are marked afterwards.
  const contentRef = useRef<HTMLDivElement | null>(null)
  // A workbook arrives as the server's PDF too (cdb_tqs#2162); the sheet grid it
  // falls back to then goes without marks rather than marking a PDF twice.
  const isPdf =
    fileType === 'pdf' || fileType === 'docx' || fileType === 'pptx' || isSheetPdfPreview(file.fileName)

  useEffect(() => {
    const root = contentRef.current
    if (!root || isPdf || showHtmlPage || isDiagram) return
    highlightWithin(root, term)
    // fileContent/loading are in the deps because the markup only exists once
    // the file has been fetched and rendered. Counting and scrolling belong to
    // the hit navigation, which reads the marks straight out of the DOM.
  }, [term, isPdf, showHtmlPage, isDiagram, fileContent, loading, fileType])

  const renderContent = () => {
    if (showHtmlPage && fileContent) {
      return (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <DriveLoadingPlaceholder message={t('common.loading')} />
            </div>
          }
        >
          <HtmlPreview fileName={file.fileName} loadHtml={loadFetchedText} />
        </Suspense>
      )
    }
    if (isDiagram && fileContent) {
      return (
        // White on purpose: a diagram is drawn on an assumed white sheet.
        <div className="flex min-h-0 flex-1 bg-white p-2">
          <Suspense
            fallback={
              <div className="flex flex-1 items-center justify-center">
                <DriveLoadingPlaceholder message={t('common.loading')} />
              </div>
            }
          >
            <BpmnPreview fileName={file.fileName} loadXml={loadFetchedText} />
          </Suspense>
        </div>
      )
    }
    switch (fileType) {
      case 'pdf':
        return (
          <PdfRenderer
            fileUrl={file.fileUrl}
            fileName={file.fileName}
            defaultFitMode={pdfDefaultFitMode}
            pageNumber={pdfInitialPage}
            onPageChange={onPdfPageChange}
            onNumPagesChange={handleNumPages}
            searchTerm={term}
          />
        )
      case 'docx':
      case 'pptx':
        // Converted server-side to PDF (issue #38) — the URL now points at
        // PDF bytes regardless of the original extension.
        return (
          <PdfRenderer
            fileUrl={file.fileUrl}
            fileName={file.fileName}
            defaultFitMode={pdfDefaultFitMode}
            pageNumber={pdfInitialPage}
            onPageChange={onPdfPageChange}
            onNumPagesChange={handleNumPages}
            searchTerm={term}
          />
        )
      case 'xlsx':
        return (
          <SheetPreview
            fileUrl={file.fileUrl}
            fileName={file.fileName}
            pdfDefaultFitMode={pdfDefaultFitMode}
            pdfInitialPage={pdfInitialPage}
            onPdfPageChange={onPdfPageChange}
            onNumPagesChange={handleNumPages}
            searchTerm={term}
          />
        )
      case 'image':
        return <ImageRenderer fileUrl={file.fileUrl} fileName={file.fileName} />
      case 'video':
        return (
          <VideoRenderer
            documentId={file.documentId || undefined}
            fileName={file.fileName}
            fileUrl={file.fileUrl || undefined}
            seekRequest={mediaSeek}
            onTimeChange={onMediaTimeChange}
          />
        )
      case 'audio':
        return (
          <div className="flex flex-1 items-center justify-center p-4">
            <MediaPlayer
              src={file.fileUrl}
              type="audio"
              title={file.name}
              fallbackDuration={mediaDuration}
              className="mx-auto w-full max-w-md"
            />
          </div>
        )
      case 'json':
        if (!fileContent) return null
        return (
          <JsonRenderer
            content={fileContent}
            isDarkTheme={isDarkTheme}
            wordWrap={wordWrap}
            showLineNumbers={showLineNumbers}
          />
        )
      case 'markdown':
        if (!fileContent) return null
        return <MarkdownRenderer content={fileContent} />
      case 'csv':
        if (!fileContent) return null
        return (
          <CsvRenderer
            content={fileContent}
            delimiter={csvDelimiter}
            hasHeader={csvHasHeader}
          />
        )
      case 'xml':
        if (!fileContent) return null
        return (
          <CodeRenderer
            content={fileContent}
            isDarkTheme={isDarkTheme}
            wordWrap={wordWrap}
            showLineNumbers={showLineNumbers}
            language="xml"
          />
        )
      case 'html':
      case 'text':
      default:
        if (!fileContent) return null
        return (
          <CodeRenderer
            content={fileContent}
            isDarkTheme={isDarkTheme}
            wordWrap={wordWrap}
            showLineNumbers={showLineNumbers}
            language={getSyntaxLanguage(file.fileName)}
          />
        )
    }
  }

  const viewOptions = (() => {
    if (fileType === 'csv') {
      return (
        <>
          {([
            { value: ',', label: ',' },
            { value: ';', label: ';' },
            { value: '\t', label: 'Tab' },
            { value: '|', label: '|' },
          ] as const).map(option => (
            <button
              key={option.value}
              onClick={() => setCsvDelimiter(option.value)}
              className="rounded px-2 py-1 text-xs font-medium transition-colors"
              style={
                csvDelimiter === option.value
                  ? { backgroundColor: 'var(--theme-interactive-color)', color: '#fff' }
                  : { backgroundColor: 'var(--window-background)', color: 'var(--text-color)' }
              }
            >
              {option.label}
            </button>
          ))}
          <button
            onClick={() => setCsvHasHeader(prev => !prev)}
            className="ml-1 flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors"
            style={
              csvHasHeader
                ? { backgroundColor: 'var(--theme-interactive-color)', color: '#fff' }
                : { backgroundColor: 'var(--window-background)', color: 'var(--text-color)' }
            }
          >
            <Hash className="h-3.5 w-3.5" />
            {t('filePreview.csvHasHeader')}
          </button>
        </>
      )
    }
    const textOptions = (
      <>
        <button
          onClick={() => setShowLineNumbers(prev => !prev)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors"
          style={
            showLineNumbers
              ? { backgroundColor: 'var(--theme-interactive-color)', color: '#fff' }
              : { backgroundColor: 'var(--window-background)', color: 'var(--text-color)' }
          }
        >
          <Hash className="h-3.5 w-3.5" />
          {t('filePreview.lineNumbers')}
        </button>
        <button
          onClick={() => setWordWrap(prev => !prev)}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors"
          style={
            wordWrap
              ? { backgroundColor: 'var(--theme-interactive-color)', color: '#fff' }
              : { backgroundColor: 'var(--window-background)', color: 'var(--text-color)' }
          }
        >
          <WrapText className="h-3.5 w-3.5" />
          {t('filePreview.wordWrap')}
        </button>
      </>
    )
    if (fileType === 'html') {
      return (
        <>
          {([
            { value: 'page', label: t('filePreview.htmlPage') },
            { value: 'source', label: t('filePreview.htmlSource') },
          ] as const).map(option => (
            <button
              key={option.value}
              type="button"
              aria-pressed={htmlView === option.value}
              onClick={() => setHtmlView(option.value)}
              className="rounded px-2 py-1 text-xs font-medium transition-colors"
              style={
                htmlView === option.value
                  ? { backgroundColor: 'var(--theme-interactive-color)', color: '#fff' }
                  : { backgroundColor: 'var(--window-background)', color: 'var(--text-color)' }
              }
            >
              {option.label}
            </button>
          ))}
          {!showHtmlPage && textOptions}
        </>
      )
    }
    if (fileType === 'text' || fileType === 'xml') return textOptions
    return null
  })()

  const isTextLike = isTextFormat(fileType)
  const toolbar =
    showToolbar && isTextLike && !isDiagram ? (
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--window-border-color)] px-3 py-2">
        {viewOptions}
        {!showHtmlPage && (
          <span className="ml-auto">
            <TextZoomControls zoom={textZoom} />
          </span>
        )}
      </div>
    ) : null

  if (error) {
    return (
      <div className={`flex min-h-0 flex-1 flex-col items-center justify-center gap-3 ${className ?? ''}`}>
        <DriveLoadingPlaceholder
          message={error}
          helperText={t('filePreview.tryAgain')}
        />
        {onDownload && (
          <Button variant="outline" onClick={onDownload} disabled={isDownloading} className="gap-2">
            <ArrowDownToLine className="h-4 w-4" />
            {t('filePreview.download')}
          </Button>
        )}
      </div>
    )
  }

  if (fileType === null) {
    // A copy of the bytes: the caller owns `file.fileUrl` and may still use it,
    // so only our own object URL is revoked after the click (R-23).
    const downloadOriginal = async () => {
      try {
        const response = await fetch(file.fileUrl)
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const copy = URL.createObjectURL(await response.blob())
        const link = document.createElement('a')
        link.href = copy
        link.download = file.fileName
        link.click()
        window.setTimeout(() => URL.revokeObjectURL(copy), 2_000)
      } catch (err) {
        logError(err as Error, { context: 'FilePreviewContent', action: 'download', fileName: file.fileName })
        setError(t('filePreview.errorLoading'))
      }
    }
    return (
      <div className={`flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center ${className ?? ''}`}>
        <div className="rounded-full border border-[var(--window-border-color)] p-4">
          <Download className="h-8 w-8 text-[var(--text-secondary)]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[var(--text-color)]">{t('filePreview.noPreviewTitle')}</p>
          <p className="mt-1 max-w-xs text-xs text-[var(--text-secondary)]">{t('filePreview.noPreviewDescription')}</p>
        </div>
        {(onDownload || file.fileUrl) && (
          <Button variant="outline" onClick={onDownload ?? downloadOriginal} disabled={isDownloading} className="gap-2">
            <ArrowDownToLine className="h-4 w-4" />
            {t('filePreview.download')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={`flex min-h-0 flex-1 flex-col ${className ?? ''}`}>
      {searchable && (
        <div className={cn('shrink-0 border-b border-[var(--window-border-color)] px-3 py-2', findBarClassName)}>
          <PreviewSearchBar search={search} className="flex-1" />
        </div>
      )}
      {toolbar}
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <DriveLoadingPlaceholder message={t('common.loading')} />
        </div>
      ) : isPdf ? (
        renderContent()
      ) : (
        <div
          ref={contentRef}
          className="flex min-h-0 flex-1 flex-col"
          style={isTextLike && !showHtmlPage && !isDiagram ? { zoom: textZoom.zoom } : undefined}
        >
          {renderContent()}
        </div>
      )}
    </div>
  )
}

/** What to show: a reference the preview loads itself (#1619), or a ready
 *  file where no reference exists (chat sources, ticket attachments, shared
 *  links, Drive's inline pane). */
type FileRefPreviewProps = Omit<PreviewViewProps, 'file'> & {
  fileRef: FileRef
  /** Called with the size of the loaded file, when it is the original's
   *  (not a converted PDF). For a window that got no size (#1667). */
  onFileSize?: (bytes: number) => void
}

type FilePreviewContentProps =
  | (Omit<PreviewViewProps, 'file'> & { file: PreviewFile; fileRef?: never; onFileSize?: never })
  | (FileRefPreviewProps & { file?: never })

export function FilePreviewContent({ file, fileRef, onFileSize, ...props }: FilePreviewContentProps) {
  if (fileRef) return <FileRefPreview fileRef={fileRef} onFileSize={onFileSize} {...props} />
  return <PreviewFileView file={file} {...props} />
}

function FileRefPreview({ fileRef, onFileSize, ...props }: FileRefPreviewProps) {
  const { t } = useTranslation()
  const state = useFileRefPreview(fileRef)
  const loadedUrl = state.status === 'ready' ? state.file.fileUrl : ''

  // The bytes are already in the browser; reading their size costs no request.
  useEffect(() => {
    if (!onFileSize || !loadedUrl || needsOfficePdfPreview(fileRef.fileName)) return
    let active = true
    fetch(loadedUrl)
      .then((response) => response.blob())
      .then((blob) => {
        if (active && blob.size > 0) onFileSize(blob.size)
      })
      .catch((err) => logError(err as Error, { context: 'FileRefPreview', action: 'fileSize', fileName: fileRef.fileName }))
    return () => {
      active = false
    }
  }, [loadedUrl, onFileSize, fileRef.fileName])

  if (state.status === 'ready') {
    // A recording is audio whatever its container (`.webm` reads as video).
    const file = fileRef.kind === 'recording' ? { ...state.file, format: 'audio' as const } : state.file
    return <PreviewFileView file={file} {...props} />
  }
  return (
    <div className={`flex min-h-0 flex-1 items-center justify-center ${props.className ?? ''}`}>
      {state.status === 'loading' ? (
        <DriveLoadingPlaceholder message={t('common.loading')} />
      ) : (
        <DriveLoadingPlaceholder message={t('filePreview.errorLoading')} helperText={t('filePreview.tryAgain')} />
      )}
    </div>
  )
}
